import { getWalletClient } from '@wagmi/core'
import { useCallback, useMemo } from 'react'
import { NATIVE_TOKEN_ADDRESS } from 'thirdweb'
import { polygon } from 'thirdweb/chains'
import { useSendTransaction } from 'thirdweb/react'
import { parseEther } from 'viem'
import constants from 'app/constants'
import { useRootContext } from 'app/services/RootProvider/RootProvider'
import { useWalletContext } from 'app/services/WalletProvider/WalletProvider'
import { thirdwebClient as client } from 'app/thread'
import { getErrorStatus } from 'app/util/errorCodes'
import mintAccessoriesWithWallet from './actions/mintAccessoriesWithWallet'
import mintAvatarWithFreeMint from './actions/mintAvatarWithFreeMint'
import mintAvatarWithWallet from './actions/mintAvatarWithWallet'
import mintEditAvatarWithFreeMint from './actions/mintEditAvatarWithFreeMint'
import mintEditAvatarWithWallet from './actions/mintEditAvatarWithWallet'

/**
 *
 * @param {object?} account - the user account data
 * @returns {object} the context object
 */
function useCheckout (account) {
  const { appActivityStatus, setAppActivityStatus } = useRootContext()
  const { getSigner, wallet } = useWalletContext()

  /**
   * Checkout accessories with a wallet (marketplace)
   */
  const checkoutAccessoriesWithWallet = useCallback(
    async data => {
      if (appActivityStatus) {
        // Defensive when busy
        return
      }

      let res = null

      if (!account?.wallet) {
        setAppActivityStatus(null)

        return {
          success: false,
          message: 'No account'
        }
      }

      setAppActivityStatus('BUSY')

      try {
        const walletClient = await getWalletClient({
          chainId: constants.CHAIN
        })

        res = await mintAccessoriesWithWallet(
          account.wallet.address,
          data,
          walletClient
        )
      } catch (e) {
        console.error(e)

        setAppActivityStatus(null)

        return getErrorStatus('UNKNOWN')
      }

      setAppActivityStatus(null)

      return res
    },
    [account, appActivityStatus]
  )

  /**
   * Checkout (create) avatar with a wallet
   */
  const checkoutAvatarWithWallet = useCallback(
    async data => {
      if (appActivityStatus) {
        // Defensive when busy
        return
      }

      let res = null

      if (!account?.wallet) {
        return {
          success: false,
          message: 'No account'
        }
      }

      setAppActivityStatus('BUSY')

      try {
        const walletClient = await getWalletClient({
          chainId: constants.CHAIN
        })

        res = await mintAvatarWithWallet(
          account.wallet.address,
          data,
          walletClient
        )
      } catch (e) {
        console.error(e)
        setAppActivityStatus(null)

        return getErrorStatus('UNKNOWN')
      }

      setAppActivityStatus(null)

      return res
    },
    [account, appActivityStatus]
  )

  /**
   * Checkout (create) avatar with a free mint - this method
   * is used for paper wallets when the price was zero
   */
  const checkoutAvatarWithFreeMint = useCallback(
    async data => {
      if (appActivityStatus) {
        // Defensive when busy
        return
      }

      let res = null

      if (!account?.wallet) {
        return {
          success: false,
          message: 'No account'
        }
      }

      setAppActivityStatus('BUSY')

      try {
        const { accessoryIds, baseIds } = data

        res = await mintAvatarWithFreeMint(
          {
            accessoryIds,
            baseIds,
            wallet: wallet?.address
          },
          getSigner
        )
      } catch (e) {
        console.error(e)
        setAppActivityStatus(null)

        return {
          success: false,
          message:
            'Free mint is currently unavailable. Please connect using traditional wallet to mint avatars for free.'
        }
      }

      setAppActivityStatus(null)

      return res
    },
    [account, wallet, appActivityStatus]
  )

  /**
   * Edit an avatar with a wallet
   */
  const editAvatarWithWallet = useCallback(
    async data => {
      if (appActivityStatus) {
        // Defensive when busy
        return
      }

      let res = null

      if (!account?.wallet) {
        return {
          success: false,
          message: 'No account'
        }
      }

      setAppActivityStatus('BUSY')

      try {
        const walletClient = await getWalletClient({
          chainId: constants.CHAIN
        })

        res = await mintEditAvatarWithWallet(data, walletClient)
      } catch (e) {
        console.error(e)
        setAppActivityStatus(null)

        return getErrorStatus('UNKNOWN')
      }

      setAppActivityStatus(null)

      return res
    },
    [account, appActivityStatus]
  )

  /**
   * Edit an avatar with a free mint - this method
   * is used for paper wallets
   */
  const editAvatarWithFreeMint = useCallback(
    async data => {
      if (appActivityStatus) {
        // Defensive when busy
        return
      }

      let res = null

      if (!account?.wallet) {
        return {
          success: false,
          message: 'No account'
        }
      }

      setAppActivityStatus('BUSY')

      try {
        res = await mintEditAvatarWithFreeMint(
          account.wallet.address,
          data,
          getSigner
        )
      } catch (e) {
        console.error(e)
        setAppActivityStatus(null)

        return getErrorStatus('UNKNOWN')
      }

      setAppActivityStatus(null)

      return res
    },
    [account, wallet, appActivityStatus]
  )

  /**
   * Checkout (create) avatar with Thirdweb Pay
   */
  const checkoutAvatarWithThirdwebPay = useCallback(
    async data => {
      if (appActivityStatus) {
        return
      }

      if (!account?.wallet) {
        return {
          success: false,
          message: 'No account'
        }
      }

      setAppActivityStatus('BUSY')

      try {
        const { createPayment } = useSendTransaction()
        const result = await createPayment({
          client,
          fiatCurrencySymbol: 'USD',
          chainId: polygon.id,
          tokenAddress: NATIVE_TOKEN_ADDRESS,
          amount: parseEther(data.total.toString()),
          buyForAddress: account.wallet.address
        })

        setAppActivityStatus(null)

        if (result && result.status === 'success') {
          return {
            success: true,
            data: result
          }
        } else {
          return {
            success: false,
            message: 'Transaction failed.'
          }
        }
      } catch (e) {
        console.error(e)
        setAppActivityStatus(null)

        return getErrorStatus('UNKNOWN')
      }
    },
    [account, appActivityStatus]
  )

  /**
   * Memo: The context object
   */
  const ctx = useMemo(
    () => ({
      checkoutAccessoriesWithWallet,
      checkoutAvatarWithWallet,
      checkoutAvatarWithFreeMint,
      checkoutAvatarWithThirdwebPay,
      editAvatarWithWallet,
      editAvatarWithFreeMint
    }),
    [
      checkoutAccessoriesWithWallet,
      checkoutAvatarWithWallet,
      checkoutAvatarWithFreeMint,
      checkoutAvatarWithThirdwebPay,
      editAvatarWithWallet,
      editAvatarWithFreeMint
    ]
  )

  return ctx
}

// Export
export default useCheckout
